// P2b — punto 2 de promociones (15-sep-2026): pantalla MÍNIMA de Promociones.
// Pegar/cargar la lista (variation_id, sale_price, fechas) → previsualizar con
// EL MISMO servicio de lectura que la tool del MCP (GET compartido, en tandas
// de 40: el tope del transporte del GET, medido contra el buffer de headers) →
// Aplicar (POST confirmar:true, el ÚNICO camino que escribe, admin+auditado) →
// historial de aplicaciones con Revertir (restaura el snapshot). Las acciones
// que escriben piden un SEGUNDO clic de confirmación en el propio botón.
import React, { useCallback, useEffect, useState } from 'react';
import Button from './ui/Button';
import { propsSoloLectura } from '../utils/soloLectura';
import { colDateTimeStr } from '../utils/colTimeUtils';
import './PromocionesTab.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const LOTE_PREVIEW = 40;   // tope del GET (medido; ver services/previsualizarPromocion del backend)
const MAX_APLICAR = 200;   // tope del POST de aplicar

// Lista pegada → items. Acepta un array JSON o líneas
// "variation_id, sale_price[, desde[, hasta]]" (separador coma/;/tab).
// sale_price vacío = retirar la oferta.
export function parsearLista(texto) {
  const t = String(texto || '').trim();
  if (!t) return { items: [], errores: ['Pega la lista primero.'] };
  if (t.startsWith('[')) {
    try {
      const arr = JSON.parse(t);
      if (!Array.isArray(arr)) return { items: [], errores: ['El JSON debe ser una lista.'] };
      return { items: arr, errores: [] };
    } catch (_) {
      return { items: [], errores: ['El JSON no es válido.'] };
    }
  }
  const items = [];
  const errores = [];
  t.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).forEach((linea, i) => {
    const partes = linea.split(/[,;\t]/).map((p) => p.trim());
    const vid = Number(partes[0]);
    if (!Number.isFinite(vid) || vid <= 0) {
      errores.push(`Línea ${i + 1}: "${partes[0]}" no es un id de variación.`);
      return;
    }
    const item = { variation_id: vid, sale_price: partes[1] != null ? partes[1] : '' };
    if (partes[2]) item.date_on_sale_from = partes[2];
    if (partes[3]) item.date_on_sale_to = partes[3];
    items.push(item);
  });
  return { items, errores };
}

export default function PromocionesTab({ soloLectura = false }) {
  const [texto, setTexto] = useState('');
  const [erroresLista, setErroresLista] = useState([]);
  const [preview, setPreview] = useState(null); // { filas, total, con_alerta } | null
  const [itemsListos, setItemsListos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [confirmando, setConfirmando] = useState(false); // 2.º clic de Aplicar
  const [aplicando, setAplicando] = useState(false);
  const [resultado, setResultado] = useState(null); // { actualizadas, fallidas } | { error }
  const [historial, setHistorial] = useState(null);
  const [revertirId, setRevertirId] = useState(null); // 2.º clic de Revertir
  // Ajuste (tanda 3): mapa variación → campaña activa que la cubre, para MOSTRAR
  // en la previsualización que una variación ya está en una campaña (con nombre)
  // en vez de sólo rechazarla al aplicar. Usa la cobertura que el backend ya calcula.
  const [cobertura, setCobertura] = useState({});

  const cargarHistorial = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/promociones`);
      const d = await r.json();
      if (r.ok) setHistorial(d.aplicaciones || []);
    } catch (_) { /* el historial no bloquea la pantalla */ }
  }, []);
  useEffect(() => { cargarHistorial(); }, [cargarHistorial]);

  const previsualizar = async () => {
    const { items, errores } = parsearLista(texto);
    setErroresLista(errores);
    setPreview(null); setResultado(null); setConfirmando(false); setItemsListos([]); setCobertura({});
    if (!items.length) return;
    if (items.length > MAX_APLICAR) {
      setErroresLista([...errores, `Son ${items.length} variaciones y el máximo por aplicación es ${MAX_APLICAR}: parte la lista.`]);
      return;
    }
    setCargando(true);
    try {
      // El GET comparte tope de transporte con la tool del MCP: tandas de 40.
      const filas = [];
      let conAlerta = 0;
      for (let i = 0; i < items.length; i += LOTE_PREVIEW) {
        const lote = items.slice(i, i + LOTE_PREVIEW);
        const r = await fetch(`${API_URL}/api/promociones/previsualizacion?items=${encodeURIComponent(JSON.stringify(lote))}`);
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'No se pudo previsualizar');
        filas.push(...(d.filas || []));
        conAlerta += d.con_alerta || 0;
      }
      // Cobertura de campañas activas (la que el backend ya calcula) — no bloquea
      // la previsualización si falla; sólo se pierde el marcador.
      let cob = {};
      try {
        const rc = await fetch(`${API_URL}/api/promociones/campanas`);
        const dc = await rc.json();
        if (rc.ok) cob = (dc.cobertura_activa && dc.cobertura_activa.por_variacion) || {};
      } catch (_) { /* la cobertura no bloquea la previsualización */ }
      setCobertura(cob);
      setPreview({ filas, total: filas.length, con_alerta: conAlerta });
      setItemsListos(items);
    } catch (e) {
      setErroresLista([e.message]);
    } finally {
      setCargando(false);
    }
  };

  const aplicar = async () => {
    if (!confirmando) { setConfirmando(true); return; } // 1.er clic: pedir confirmación
    setAplicando(true); setResultado(null);
    try {
      const r = await fetch(`${API_URL}/api/promociones/aplicar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: itemsListos, confirmar: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'No se pudo aplicar');
      setResultado(d);
      setConfirmando(false);
      cargarHistorial();
    } catch (e) {
      setResultado({ error: e.message });
    } finally {
      setAplicando(false);
    }
  };

  const revertir = async (id) => {
    if (revertirId !== id) { setRevertirId(id); return; } // 1.er clic
    try {
      const r = await fetch(`${API_URL}/api/promociones/${id}/revertir`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'No se pudo revertir');
      setResultado({ ...d, revertida: true });
    } catch (e) {
      setResultado({ error: e.message });
    } finally {
      setRevertirId(null);
      cargarHistorial();
    }
  };

  // Hora de COLOMBIA vía el helper espejo — rebanar el ISO crudo mostraba UTC
  // (una aplicación del 15 a las 21:52 salía como del 16 a las 02:52).
  const fechaCorta = (v) => (v ? colDateTimeStr(v) : '');

  return (
    <div className="promo-tab">
      <section className="promo-card">
        <h3>Aplicar precios de promoción</h3>
        <p className="promo-hint">
          Pega una línea por variación: <code>id_variación, precio_oferta[, desde[, hasta]]</code> (fechas
          AAAA-MM-DD; precio vacío retira la oferta), o un JSON con la misma forma. Primero se
          previsualiza — nada se escribe hasta que confirmes Aplicar.
        </p>
        <textarea
          className="promo-input" rows={6} value={texto} aria-label="Lista de variaciones y precios"
          placeholder={'11041, 36000, 2026-09-20, 2026-09-30\n11042, 36000\n11043,'}
          onChange={(e) => { setTexto(e.target.value); setConfirmando(false); }}
        />
        {erroresLista.map((e, i) => <p key={i} className="promo-error" role="alert">{e}</p>)}
        <div className="promo-actions">
          <Button onClick={previsualizar} loading={cargando}>Previsualizar</Button>
          {preview && (
            <Button variant="primary" {...propsSoloLectura(soloLectura)} onClick={aplicar} loading={aplicando}>
              {confirmando ? `¿Seguro? Aplicar ${preview.total} precios` : `Aplicar ${preview.total} precios`}
            </Button>
          )}
        </div>

        {preview && (
          <>
            <p className="promo-resumen">
              {preview.total} {preview.total === 1 ? 'variación' : 'variaciones'} ·{' '}
              {preview.con_alerta > 0
                ? <strong className="promo-alerta-num">{preview.con_alerta} con alerta — revisa antes de aplicar</strong>
                : 'sin alertas'}
            </p>
            <div className="promo-tabla" role="table" aria-label="Previsualización de la promoción">
              <div className="promo-fila promo-fila--head" role="row">
                <span>Producto</span><span>Variación</span><span>Lista</span>
                <span>Oferta actual</span><span>Oferta nueva</span><span>% desc.</span><span>Alertas</span>
              </div>
              {preview.filas.map((f, i) => (
                <div key={`${f.variation_id}-${i}`} className={`promo-fila${f.alertas.length ? ' promo-fila--alerta' : ''}`} role="row">
                  <span>{f.producto || `#${f.variation_id}`}</span>
                  <span>{f.variacion || '—'}</span>
                  <span>{f.regular_price || '—'}</span>
                  <span>{f.sale_price_actual || '—'}</span>
                  <span>{f.sale_price_nuevo === '' ? 'retira la oferta' : f.sale_price_nuevo}</span>
                  <span>{f.descuento_pct != null ? `${f.descuento_pct}%` : '—'}</span>
                  <span>
                    {f.alertas.length ? f.alertas.join(' · ') : '—'}
                    {cobertura[f.variation_id] && (
                      <strong className="promo-en-campana"> · En campaña activa «{cobertura[f.variation_id].nombre}»</strong>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {resultado && (
          <p className={`promo-resultado${resultado.error ? ' promo-error' : ''}`} role="status">
            {resultado.error
              ? resultado.error
              : resultado.revertida
                ? `Reversión hecha: ${resultado.actualizadas.length} variaciones restauradas${resultado.fallidas.length ? `, ${resultado.fallidas.length} fallidas` : ''}.`
                : `Promoción aplicada: ${resultado.actualizadas.length} variaciones actualizadas${resultado.fallidas.length ? `, ${resultado.fallidas.length} fallidas` : ''}.`}
          </p>
        )}
      </section>

      <section className="promo-card">
        <h3>Aplicaciones anteriores</h3>
        <p className="promo-hint">Revertir restaura los precios y fechas que había ANTES de esa aplicación (queda su propio registro, así que también se puede deshacer).</p>
        {historial === null ? <p className="promo-hint">Cargando…</p>
          : historial.length === 0 ? <p className="promo-hint">Todavía no hay aplicaciones.</p>
            : (
              <div className="promo-tabla" role="table" aria-label="Historial de promociones">
                <div className="promo-fila promo-fila--head" role="row">
                  <span>Fecha</span><span>Quién</span><span>Origen</span><span>Variaciones</span><span>Estado</span><span />
                </div>
                {historial.map((h) => (
                  <div key={h.id} className="promo-fila" role="row">
                    <span>{fechaCorta(h.aplicadaEn)}</span>
                    <span>{h.usuario}</span>
                    <span>{h.herramienta}</span>
                    <span>{h.actualizadas} ok{h.fallidas ? ` · ${h.fallidas} fallidas` : ''}</span>
                    <span>{h.abortada ? 'abortada' : h.revertidaEn ? `revertida ${fechaCorta(h.revertidaEn)}` : 'aplicada'}</span>
                    <span>
                      {!h.revertidaEn && !h.abortada && (
                        <Button size="sm" onClick={() => revertir(h.id)}>
                          {revertirId === h.id ? '¿Seguro? Revertir' : 'Revertir'}
                        </Button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            )}
      </section>
    </div>
  );
}
